import { AssessmentType, SurveyStatus } from '@@types/api.types';
import { useAssessmentsQueryOptions } from '@queries/assessment.query';
import { useMissionAssessmentVersionsByTechnologyQueryOptions } from '@queries/mission-assessments.query';
import { useGetSurveysQueryOptions } from '@queries/survey.query';
import {
  useGetAssessmentHistoryQueryOptions,
  useGetTechnology,
  useGetTechnologyMrlAssessmentQueryOptions,
  useGetTechnologyTrlAssessmentQueryOptions,
  useGetTechnologyTrlProjectionQueryOptions,
} from '@queries/technology.query';
import { useCurrentUserQueryOptions, useGetCurrentUser } from '@queries/users.query';
import { useQueryClient } from '@tanstack/react-query';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { useEffect, useRef } from 'react';

export const usePrefetchReadinessData = (technologyId?: string): void => {
  const queryClient = useQueryClient();
  const prefetchedForIdRef = useRef<string | null>(null);

  // Only prefetch if technology exists (prevents prefetching for non-existent technologies)
  const { data: technology, isError: isTechnologyError } = useGetTechnology(technologyId);
  const { isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();

  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id,
  });

  // TRL query options (only create when technology exists)
  const validTechnologyId = technology && !isTechnologyError ? technologyId : undefined;

  const trlAssessmentsQueryOptions = useAssessmentsQueryOptions({
    technologyId: validTechnologyId,
    type: [AssessmentType.TRL],
  });
  const featuredTrlAssessmentQueryOptions = useGetTechnologyTrlAssessmentQueryOptions(validTechnologyId);
  const trlProjectionQueryOptions = useGetTechnologyTrlProjectionQueryOptions(validTechnologyId);

  // MRL query options
  const mrlAssessmentsQueryOptions = useAssessmentsQueryOptions({
    technologyId: validTechnologyId,
    type: [AssessmentType.MRL],
  });
  const featuredMrlAssessmentQueryOptions = useGetTechnologyMrlAssessmentQueryOptions(validTechnologyId);

  // MA query options
  const maVersionsQueryOptions = useMissionAssessmentVersionsByTechnologyQueryOptions(
    isChildOrganizationView ? undefined : validTechnologyId,
  );

  // Shared query options (draft surveys used by both TRL and MRL)
  const surveysQueryOptions = useGetSurveysQueryOptions({
    status: SurveyStatus.DRAFT,
    technologyId: validTechnologyId,
  });

  // Assessment history query options (used by both TRL and MRL overview)
  const assessmentHistoryQueryOptions = useGetAssessmentHistoryQueryOptions(validTechnologyId);

  // Current user query options (used by multiple components)
  const currentUserQueryOptions = useCurrentUserQueryOptions();

  // Prefetch queries only once per technology
  useEffect(() => {
    // Skip if no valid technology or already prefetched for this specific technology
    if (!validTechnologyId || prefetchedForIdRef.current === validTechnologyId) return;

    prefetchedForIdRef.current = validTechnologyId;

    // TRL prefetches
    void queryClient.prefetchQuery(trlAssessmentsQueryOptions);
    void queryClient.prefetchQuery(featuredTrlAssessmentQueryOptions);
    void queryClient.prefetchQuery(trlProjectionQueryOptions);

    // MRL prefetches
    void queryClient.prefetchQuery(mrlAssessmentsQueryOptions);
    void queryClient.prefetchQuery(featuredMrlAssessmentQueryOptions);

    // MA prefetches
    void queryClient.prefetchQuery(maVersionsQueryOptions);

    // Shared prefetches
    void queryClient.prefetchQuery(surveysQueryOptions);
    void queryClient.prefetchQuery(assessmentHistoryQueryOptions);
    void queryClient.prefetchQuery(currentUserQueryOptions);
  }, [
    assessmentHistoryQueryOptions,
    currentUserQueryOptions,
    featuredMrlAssessmentQueryOptions,
    trlProjectionQueryOptions,
    featuredTrlAssessmentQueryOptions,
    maVersionsQueryOptions,
    mrlAssessmentsQueryOptions,
    queryClient,
    surveysQueryOptions,
    trlAssessmentsQueryOptions,
    validTechnologyId,
  ]);
};
